import { FC, useState } from 'react';

import { router } from '@inertiajs/react';
import { Bell, ChevronDown, ChevronUp } from 'lucide-react';

import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../../components/ui/Card';
import { Checkbox } from '../../components/ui/Checkbox';
import { Label } from '../../components/ui/Label';
import { profileNotificationSettingsRoute } from '../../models/routes';

export type NotificationSetting = {
    event: string;
    label: string;
    email_enabled: boolean;
    telegram_enabled: boolean;
};

interface Props {
    settings: NotificationSetting[];
    isOpen: boolean;
    onToggle: () => void;
    telegramConnected: boolean;
    error?: string;
}

type Channel = 'email_enabled' | 'telegram_enabled';

export const NotificationSettingsForm: FC<Props> = ({
    settings,
    isOpen,
    onToggle,
    telegramConnected,
    error,
}) => {
    const [updatingKey, setUpdatingKey] = useState<string | null>(null);

    const handleChange = (
        setting: NotificationSetting,
        channel: Channel,
        enabled: boolean,
    ) => {
        setUpdatingKey(`${setting.event}:${channel}`);
        router.patch(
            profileNotificationSettingsRoute(),
            {
                notification_setting: {
                    event: setting.event,
                    email_enabled:
                        channel === 'email_enabled'
                            ? enabled
                            : setting.email_enabled,
                    telegram_enabled:
                        channel === 'telegram_enabled'
                            ? enabled
                            : setting.telegram_enabled,
                },
            },
            {
                preserveScroll: true,
                preserveState: true,
                only: ['notification_settings', 'errors'],
                onFinish: () => setUpdatingKey(null),
            },
        );
    };

    return (
        <Card className="h-fit">
            <CardHeader
                className="cursor-pointer transition-colors hover:bg-muted/50"
                onClick={onToggle}
            >
                <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Bell className="h-5 w-5 text-primary" />
                        Налаштування сповіщень
                    </div>
                    {isOpen ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                </CardTitle>
                <CardDescription>
                    Керуйте email та Telegram-сповіщеннями для різних подій
                </CardDescription>
            </CardHeader>

            {isOpen && (
                <CardContent className="space-y-4">
                    {error && <p className="text-sm text-red-600">{error}</p>}

                    {!telegramConnected && (
                        <div className="rounded-xl border bg-muted/20 p-3 text-sm text-muted-foreground">
                            Telegram-сповіщення почнуть надходити після
                            підключення Telegram-бота.
                        </div>
                    )}

                    <div className="space-y-3">
                        {settings.map((setting) => (
                            <div
                                key={setting.event}
                                className="rounded-xl border bg-muted/20 p-4"
                            >
                                <p className="text-sm font-medium">
                                    {setting.label}
                                </p>
                                <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:gap-6">
                                    <Label className="flex items-center gap-2 text-sm font-normal">
                                        <Checkbox
                                            checked={setting.email_enabled}
                                            disabled={
                                                updatingKey ===
                                                `${setting.event}:email_enabled`
                                            }
                                            onCheckedChange={(checked) =>
                                                handleChange(
                                                    setting,
                                                    'email_enabled',
                                                    checked === true,
                                                )
                                            }
                                        />
                                        Email
                                    </Label>

                                    {telegramConnected && (
                                        <Label className="flex items-center gap-2 text-sm font-normal">
                                            <Checkbox
                                                checked={setting.telegram_enabled}
                                                disabled={
                                                    updatingKey ===
                                                    `${setting.event}:telegram_enabled`
                                                }
                                                onCheckedChange={(checked) =>
                                                    handleChange(
                                                        setting,
                                                        'telegram_enabled',
                                                        checked === true,
                                                    )
                                                }
                                            />
                                            Telegram
                                        </Label>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            )}
        </Card>
    );
};
